/**
 * Shift roster view model — pure derivation.
 *
 * The week as the plant's own schedule sheet draws it: a row for each line ×
 * shift, the people whose base that line and shift is, and per day the batches
 * the shift runs. A batch that crosses the shift change is on both shifts'
 * rows (each with its own part); the part after the line's last shift is that
 * shift's overtime. A batch names only the people who are NOT the row's own
 * crew — the day's reallocations. Times are decimal hours from Monday 00:00.
 */
import { LineShift } from "../../../production-plan/utils/line-shifts";
import { toLocalIso } from "../../../utils/time";
import { LaneCard, LaneCrewGroup } from "./lane-rows";
import { Timeline, TimelineStep } from "./timeline";

export interface CrewMember {
	workerId: string;
	workerName: string;
	workstation: string;
	/** "1st", "2nd", "12h day" … null when the person's crew is not known. */
	shiftLabel: string | null;
}

export interface Window {
	start: number;
	end: number;
}

export interface CrewItem {
	key: string;
	step: TimelineStep;
	/** Where a drop on the item lands: the first crew line of the step that has a phase. */
	group: LaneCrewGroup | null;
	/** This shift's part of the batch. */
	start: number;
	end: number;
	/** The batch began on an earlier shift / goes on into a later one. */
	continuesFrom: boolean;
	continuesTo: boolean;
	/** Hours the batch runs past the line's last shift that day (finished on overtime). */
	overtimeHours: number;
	/** No shift of the line covers this batch at all that day. */
	offShift: boolean;
	/** People on this part who are the row's own crew, and who are not (reallocated). */
	crew: LaneCard[];
	away: LaneCard[];
	/** Heads still missing on this part. */
	open: number;
}

export interface CrewRow {
	key: string;
	workcenter: string;
	shift: string;
	/** Typical hours of the shift ("07:00–15:30"), for the row's label. */
	hours: string;
	/** The shift's window on each day of the week (null: the shift does not run that day). */
	windows: (Window | null)[];
	crew: CrewMember[];
	/** Batches per day. */
	days: CrewItem[][];
}

export interface CrewLine {
	workcenter: string;
	rows: CrewRow[];
}

const DAY = 24;
/** A part shorter than this at a shift's edge (a handover overlap) is not drawn on that shift. */
const MIN_PART_HOURS = 1;
const DEFAULT_SHIFTS: { label: string; start: number; end: number }[] = [
	{ label: "1st", start: 7, end: 15.5 },
	{ label: "2nd", start: 15, end: 23.5 },
];

const hoursFrom = (weekStart: Date, iso: string) => (new Date(iso).getTime() - weekStart.getTime()) / 3600000;
const overlaps = (a: Window, b: Window) => a.start < b.end && b.start < a.end;
const hhmm = (hours: number) => {
	const h = ((Math.floor(hours) % DAY) + DAY) % DAY;
	const m = Math.round((hours - Math.floor(hours)) * 60);
	return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/** The line's shifts: label → its window on each day, in the order the shifts start. */
export function lineWindows(
	workcenter: string,
	lineShifts: LineShift[],
	weekStart: Date,
	days: number
): { label: string; hours: string; windows: (Window | null)[] }[] {
	const mine = lineShifts.filter((s) => s.workstation.toLowerCase() === workcenter.toLowerCase());
	if (mine.length === 0) {
		return DEFAULT_SHIFTS.map((s) => ({
			label: s.label,
			hours: `${hhmm(s.start)}–${hhmm(s.end)}`,
			windows: Array.from({ length: days }, (_, d) => ({ start: d * DAY + s.start, end: d * DAY + s.end })),
		}));
	}
	const byLabel = new Map<string, LineShift[]>();
	mine.forEach((s) => byLabel.set(s.label, [...(byLabel.get(s.label) ?? []), s]));
	return Array.from(byLabel.entries())
		.map(([label, list]) => {
			const windows: (Window | null)[] = Array.from({ length: days }, () => null);
			list.forEach((s) => {
				const start = hoursFrom(weekStart, s.start);
				const day = Math.floor(start / DAY);
				if (day >= 0 && day < days && !windows[day]) windows[day] = { start, end: hoursFrom(weekStart, s.end) };
			});
			const first = list[0];
			const startOfDay = ((hoursFrom(weekStart, first.start) % DAY) + DAY) % DAY;
			return { label, hours: `${first.start.slice(11, 16)}–${first.end.slice(11, 16)}`, windows, startOfDay };
		})
		.sort((a, b) => a.startOfDay - b.startOfDay)
		.map(({ label, hours, windows }) => ({ label, hours, windows }));
}

/**
 * A person counts on a part of a batch when they work a real share of it: an
 * hour, or all of a shorter part, or all of their own shorter block. The half
 * hour a 12-hour day crew overlaps the night shift (19:00–19:30) does not put
 * the day crew on the night row.
 */
export const worksOnPart = (card: Window, part: Window): boolean => {
	const shared = Math.min(card.end, part.end) - Math.max(card.start, part.start);
	if (shared <= 0) return false;
	return shared >= Math.min(MIN_PART_HOURS, part.end - part.start, card.end - card.start) - 1e-9;
};

/** The people on a part of a batch, once each (a block stored in pieces is one person). */
const cardsIn = (step: TimelineStep, part: Window) => {
	const seen = new Set<string>();
	return step.groups
		.flatMap((g) => g.cards)
		.filter((c) => worksOnPart(c, part))
		.filter((c) => {
			if (seen.has(c.workerId)) return false;
			seen.add(c.workerId);
			return true;
		});
};

function item(step: TimelineStep, part: Window, home: Set<string>, extra: Partial<CrewItem>): CrewItem {
	const cards = cardsIn(step, part);
	const people = new Set(cards.filter((c) => !c.isOut).map((c) => c.workerId));
	const needed = step.groups.filter((g) => overlaps(g, part)).reduce((sum, g) => sum + g.needed, 0);
	return {
		key: `${step.workOrderId}:${part.start}`,
		step,
		group: step.groups.find((g) => g.dropPhaseId !== null && overlaps(g, part)) ?? step.groups.find((g) => g.dropPhaseId !== null) ?? null,
		start: part.start,
		end: part.end,
		continuesFrom: step.start < part.start,
		continuesTo: step.end > part.end,
		overtimeHours: 0,
		offShift: false,
		crew: cards.filter((c) => home.has(c.workerId)),
		away: cards.filter((c) => !home.has(c.workerId)),
		open: Math.max(0, needed - people.size),
		...extra,
	};
}

/**
 * The shift roster for a week (or the days asked for): every line on the roster,
 * a row per shift the line works, its crew, and each day's batches on each shift.
 */
export function buildCrewBoard(timeline: Timeline, lineShifts: LineShift[], crews: CrewMember[], weekStart: Date, days = 7): CrewLine[] {
	return timeline.lanes.map((lane) => {
		const shifts = lineWindows(lane.workcenter, lineShifts, weekStart, days);
		const rows: CrewRow[] = shifts.map((shift) => {
			const crew = crews.filter(
				(m) => m.workstation.toLowerCase() === lane.workcenter.toLowerCase() && (m.shiftLabel ?? "") === shift.label
			);
			return {
				key: `${lane.workcenter}|${shift.label}`,
				workcenter: lane.workcenter,
				shift: shift.label,
				hours: shift.hours,
				windows: shift.windows,
				crew,
				days: Array.from({ length: days }, () => []),
			};
		});
		// Line people with a crew that is not one of the line's shifts go on its first row.
		const placed = new Set(rows.flatMap((r) => r.crew.map((m) => m.workerId)));
		const others = crews.filter((m) => m.workstation.toLowerCase() === lane.workcenter.toLowerCase() && !placed.has(m.workerId));
		if (rows.length > 0) rows[0].crew = [...rows[0].crew, ...others];

		for (let d = 0; d < days; d++) {
			const dayWindow = { start: d * DAY, end: (d + 1) * DAY };
			lane.steps.forEach((step) => {
				// The rows the batch touches that day, each with its part.
				const candidates = rows
					.map((row, i) => {
						const w = row.windows[d];
						if (!w || !overlaps(step, w)) return null;
						const part = { start: Math.max(step.start, w.start), end: Math.min(step.end, w.end) };
						const length = part.end - part.start;
						// A handover overlap is not a part of its own, unless it is the whole batch.
						if (length < MIN_PART_HOURS && step.end - step.start > length) return null;
						return { i, w, item: item(step, part, new Set(row.crew.map((m) => m.workerId)), {}) };
					})
					.filter((c): c is { i: number; w: Window; item: CrewItem } => c !== null);
				// Shifts that overlap in time (a 12-hour crew beside the 2nd shift): the part
				// belongs to the row whose own crew is on it; another row only shows time
				// none of those rows covers.
				const withCrew = candidates.filter((c) => c.item.crew.length > 0);
				const kept = withCrew.length > 0 ? [...withCrew] : candidates.filter((c) => c.w.start <= step.start && c.w.end > step.start).slice(0, 1);
				if (kept.length === 0 && candidates.length > 0) kept.push(candidates[0]);
				candidates
					.filter((c) => !kept.includes(c))
					.sort((x, y) => x.item.start - y.item.start)
					.forEach((c) => {
						const covered = kept.reduce((sum, k) => sum + Math.max(0, Math.min(k.item.end, c.item.end) - Math.max(k.item.start, c.item.start)), 0);
						if (c.item.end - c.item.start - covered >= MIN_PART_HOURS) kept.push(c);
					});
				kept.forEach((c) => rows[c.i].days[d].push(c.item));
				const shown = kept.length > 0;

				// After the line's last shift that day: overtime on the shift it runs on from.
				const lastEnd = Math.max(...rows.map((r) => r.windows[d]?.end ?? -Infinity));
				const runsOn = kept.slice().sort((x, y) => y.w.end - x.w.end)[0];
				if (runsOn && runsOn.w.end === lastEnd && step.start < lastEnd && step.end > lastEnd) {
					const nextStart = Math.min(...rows.map((r) => r.windows[d + 1]?.start ?? Infinity), (d + 1) * DAY + 7);
					const end = Math.min(step.end, nextStart);
					const mine = runsOn.item;
					if (end > lastEnd) {
						mine.overtimeHours = Math.round((end - lastEnd) * 10) / 10;
						mine.end = end;
						mine.continuesTo = step.end > end;
						const extra = item(step, { start: lastEnd, end }, new Set(rows[runsOn.i].crew.map((m) => m.workerId)), {});
						extra.away.forEach((c) => {
							if (!mine.away.some((x) => x.workerId === c.workerId) && !mine.crew.some((x) => x.workerId === c.workerId)) mine.away.push(c);
						});
					}
				}
				// A batch no shift of the line covers that day (a weekend run): on the first row, flagged.
				const startsToday = step.start >= dayWindow.start && step.start < dayWindow.end;
				if (!shown && startsToday && rows.length > 0 && !rows.some((r) => r.days[d].some((x) => x.step.workOrderId === step.workOrderId))) {
					const part = { start: step.start, end: Math.min(step.end, dayWindow.end + 7) };
					rows[0].days[d].push(item(step, part, new Set(rows[0].crew.map((m) => m.workerId)), { offShift: true }));
				}
			});
			rows.forEach((row) => row.days[d].sort((a, b) => a.start - b.start));
		}
		return { workcenter: lane.workcenter, rows };
	});
}

/** Lines with work on the days shown first, then the idle ones; each group keeps its order. */
export function linesWithPlanFirst(lines: CrewLine[], days: number[]): CrewLine[] {
	const busy = (line: CrewLine) => line.rows.some((row) => days.some((d) => (row.days[d]?.length ?? 0) > 0));
	return [...lines.filter(busy), ...lines.filter((line) => !busy(line))];
}

/** The local ISO stamp of a point given in hours from the week's Monday 00:00. */
export const isoAt = (weekStart: Date, hours: number): string => toLocalIso(new Date(weekStart.getTime() + hours * 3600000));

/** "+2.5 h OT" */
export const fmtOvertime = (hours: number): string => `+${hours.toFixed(1).replace(/\.0$/, "")} h OT`;

/** "07:00–13:00" for a part of a batch (hours from Monday 00:00). */
export const partHours = (start: number, end: number) => `${hhmm(start)}–${hhmm(end)}`;
